import * as React from 'react'
import { compose, ChildProps } from 'react-apollo'
import { withRouter } from 'react-router-dom'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import { History } from 'history'
import { ApolloClient, ApolloError } from 'apollo-client'
import { NormalizedCache } from 'apollo-cache-inmemory'
import * as Modal from 'react-modal'
import * as R from 'ramda'
import { Store, getUserInfo, UserInfo } from '../../reducers'
import { EditableCase } from '../../reducers/project'

import ProjectFormHelper from './ProjectFormHelper'
import {
  withProjectParameters,
  ProjectParametersResponse
} from './projectParameterQueries'

import {
  SpinnerDiv,
  ModalHead,
  ModalButtonPrimary,
  ModalButtonNormal,
  ModalTools,
  DetailRow,
  DetailRowBig,
  DescriptionRow,
  ModalRow,
  modalStyle,
  ErrorMsg
} from './Styles'
import {
  CREATE_PROJECT,
  ProjectType,
  PROJECTS_QUERY,
  getProjects as getProjectsQuery,
  ProjectsResult
} from './projectQueries'
import { loadProject } from '../../actions/project'
import { Action } from '../../actions/types'
import { checkParams, FormDataParams } from '.'
import { CountryType, withCountries } from '../Shared/CountryOptions'
import { recordEvent } from '../../analytics'
import Error from '../Shared/Alerts/Error'
import {
  convertBeamExtra,
  convertBeamMaterial,
  convertConcreteGrades,
  convertFireResistance,
  filterConcreteGrades,
  filterFireResistance,
  isLocationNorthAmerica
} from 'src/location_changes/conversionHelper'

if (process.env.NODE_ENV !== 'test') {
  Modal.setAppElement('#root')
}

type ClientContext = {
  client: ApolloClient<NormalizedCache>
}

type ApolloQueryProps = {
  loading: boolean
  error?: ApolloError
}

type GetProjectsProps = {
  getProjects: ProjectsResult
} & ApolloQueryProps

type ProjectFormData = {
  name: string
  description: string
  location: string
} & FormDataParams

export type InitialProjectParameters = {
  name: string
  description: string
  location: string
  fireResistance: string
  beamMaterial: string
  beamConcreteGrade: string
  beamExtra: string
  execution: string
  finishing: string
  spaceType: string
}

type InputProps = {
  history: History
  isOpen?: boolean

  cases?: EditableCase[]
  initValues?: InitialProjectParameters
  spaceType?: string
  projectLocation?: string
  userData: UserInfo
  countries: CountryType[]

  closeDialog: () => void
  loadProject: (project: ProjectType) => Action
} & GetProjectsProps &
  ApolloQueryProps

type State = {
  formData: ProjectFormData
  busy: boolean
  confirmModal: boolean
  error: string | null
}

const defState: State = {
  formData: {
    name: 'New Project',
    description: '',
    location: '',
    spaceType: '',
    fireResistance: '',
    beamMaterial: '',
    beamConcreteGrade: '',
    beamExtra: '',
    execution: '',
    finishing: ''
  },
  busy: false,
  confirmModal: false,
  error: null
}

const fieldNames = {
  name: 'Project name',
  location: 'Location',
  created: 'Created',
  modified: 'Modified',
  fireResistance: 'Fire resistance',
  beamMaterial: 'Materials',
  beamMaterialNA: 'Steel',
  beamConcreteGrade: '',
  beamConcreteGradeNA: 'Infill concrete',
  beamExtra: '',
  beamExtraNA: 'Fire rebars',
  execution: 'Execution class',
  finishing: 'Finishing',
  description: 'Description',
  spaceType: 'Space type'
}

const checkRequiredTextField = (str: string) => {
  return typeof str === 'string' && str.length > 0
}

class NewProjectDialog extends React.Component<
  ChildProps<InputProps, ProjectParametersResponse>,
  State
> {
  static contextTypes = {
    client: () => null
  }

  context: ClientContext
  MyForm: ProjectFormHelper

  constructor(props: InputProps) {
    super(props)

    this.state = defState
    this.MyForm = new ProjectFormHelper({
      setState: (changed: object) => {
        const newFormData: object = {
          formData: {
            ...this.state.formData,
            ...changed
          }
        }
        this.setState(newFormData)
      },
      editMode: true,
      fieldNames
    })
  }

  componentWillReceiveProps(
    nextProps: ChildProps<InputProps, ProjectParametersResponse>
  ) {
    const initValues = nextProps.initValues || {}
    const formData = {
      ...defState.formData,
      beamMaterial:
        (nextProps.data &&
          nextProps.data.beamMaterials &&
          nextProps.data.beamMaterials[0].id) ||
        '',
      beamExtra:
        (nextProps.data &&
          nextProps.data.beamExtras &&
          nextProps.data.beamExtras[0].id) ||
        '',
      ...initValues
    }
    let country = nextProps.userData
      ? nextProps.userData.country
      : formData.location

    const ud = nextProps.userData
    if (ud && this.props.countries) {
      // A hotfix for the issue where the country is saved as the countryname (not code)
      const selectedIndx = R.findIndex(
        opt => opt.displayName === ud.country,
        this.props.countries
      )

      if (selectedIndx !== -1) {
        country = this.props.countries[selectedIndx].id
      }
    } else {
      country = formData.location
    }
    formData.location = nextProps.projectLocation || country
    formData.spaceType = nextProps.spaceType || formData.spaceType

    this.setState({ formData })
  }

  checkNewProjectParams() {
    const { formData } = this.state
    if (!this.props.data) {
      return false
    }
    const { name } = formData
    return (
      checkRequiredTextField(name) && checkParams(formData, this.props.data)
    )
  }

  onSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    const { history, cases = [], closeDialog } = this.props
    const { formData } = this.state
    const {
      name,
      location,
      spaceType,
      description,
      fireResistance,
      beamMaterial,
      beamConcreteGrade,
      beamExtra,
      execution,
      finishing
    } = formData
    const project = { name, description, location, spaceType }
    const parameters = {
      fireResistance,
      beamMaterial,
      beamConcreteGrade,
      beamExtra,
      execution,
      finishing
    }
    const client = this.context.client
    const projectCases = cases.map((projectCase: EditableCase) => {
      return {
        id: projectCase.id,
        name: projectCase.name,
        description: projectCase.description,
        new: projectCase._new,
        data: JSON.stringify({
          touchedCategories: projectCase.touchedCategories,
          activeItem: projectCase.activeItem,
          loads: projectCase.loadCases,
          beams: projectCase.beams,
          slabs: projectCase.slabs
        })
      }
    })

    this.setState({ busy: true, error: '' }, () => {
      client
        .mutate<Response>({
          mutation: CREATE_PROJECT,
          variables: {
            project: project,
            params: JSON.stringify(parameters),
            activeCase: null,
            cases: projectCases.length > 0 ? projectCases : null
          },
          refetchQueries: [{ query: PROJECTS_QUERY }]
        })
        .then(({ data }) => {
          this.setState({ busy: false, error: '' })
          closeDialog()
          recordEvent('project', 'created')
          this.props.loadProject(data.createProject)
          history.push(`/project/${data.createProject.id}/workspace`)
        })
        .catch(err => {
          console.warn('Save failed', err)
          this.setState({ busy: false, error: 'Save failed' })
        })
    })

    e.preventDefault()
  }

  render() {
    const {
      loading,
      error,
      isOpen = false,
      closeDialog,
      cases,
      data,
      getProjects,
      countries
    } = this.props
    const { formData } = this.state
    const {
      name,
      spaceType,
      description,
      location,
      fireResistance,
      beamMaterial,
      beamConcreteGrade,
      beamExtra,
      execution,
      finishing
    } = formData
    const MyForm = this.MyForm

    const nameUnique = !R.contains(
      name,
      R.map(proj => proj.name, getProjects || [])
    )
    const paramsOk = this.checkNewProjectParams() && nameUnique

    if (
      !data ||
      !data.spaceTypes ||
      !data.fireResistances ||
      !data.executionTypes ||
      !data.finishingTypes ||
      !data.beamConcreteGrades ||
      !data.beamMaterials ||
      !data.beamExtras ||
      !countries
    ) {
      return null
    }

    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={closeDialog}
        style={modalStyle}
        shouldCloseOnOverlayClick={false}
      >
        <SpinnerDiv>
          {loading && <div className="spinner" />}
          <ModalHead>{cases ? 'Save project' : 'New project'}</ModalHead>
          {(error || !nameUnique) && (
            <ErrorMsg>{!nameUnique ? 'Name already in use!' : error}</ErrorMsg>
          )}
          {this.state.error && (
            <Error onDismiss={() => this.setState({ error: null })}>
              {this.state.error}
            </Error>
          )}
          <form
            onSubmit={this.onSubmit}
            style={{ padding: '0 3em', display: 'grid' }}
          >
            <DetailRowBig>
              {MyForm.renderEditField(
                'name',
                name,
                null,
                true,
                true,
                true,
                !nameUnique
              )}
            </DetailRowBig>
            <DetailRow>
              {MyForm.renderSelectField(
                'location',
                location,
                countries,
                '13em',
                true
              )}
            </DetailRow>
            <DetailRow>
              {!isLocationNorthAmerica() &&
                MyForm.renderSelectField(
                  'spaceType',
                  spaceType,
                  data.spaceTypes,
                  '13em',
                  true
                )}
              {MyForm.renderSelectField(
                !isLocationNorthAmerica() ? 'beamMaterial' : 'beamMaterialNA',
                beamMaterial,
                data.beamMaterials.map((option, i) => ({
                  displayName: convertBeamMaterial(option.displayName),
                  id: option.id
                })),
                '13em',
                true
              )}
              {MyForm.renderSelectField(
                !isLocationNorthAmerica()
                  ? 'beamConcreteGrade'
                  : 'beamConcreteGradeNA',
                beamConcreteGrade,
                filterConcreteGrades(data.beamConcreteGrades).map(
                  (option, i) => ({
                    displayName: convertConcreteGrades(option.displayName),
                    id: option.id
                  })
                ),
                '13em',
                true
              )}
              {MyForm.renderSelectField(
                !isLocationNorthAmerica() ? 'beamExtra' : 'beamExtraNA',
                beamExtra,
                data.beamExtras.map((option, i) => ({
                  displayName: convertBeamExtra(option.displayName),
                  id: option.id
                })),
                '13em',
                true
              )}
            </DetailRow>
            <DetailRow>
              {MyForm.renderSelectField(
                'fireResistance',
                fireResistance,
                filterFireResistance(data.fireResistances).map((option, i) => ({
                  displayName: convertFireResistance(option.displayName),
                  id: option.id
                })),
                '13em',
                true
              )}
              {!isLocationNorthAmerica() &&
                MyForm.renderSelectField(
                  'execution',
                  execution,
                  data.executionTypes,
                  '13em',
                  true
                )}
              {!isLocationNorthAmerica() &&
                MyForm.renderSelectField(
                  'finishing',
                  finishing,
                  data.finishingTypes,
                  '13em',
                  true
                )}
            </DetailRow>
            <DescriptionRow>
              {MyForm.renderEditTextArea(
                'description',
                description,
                null,
                false
              )}
            </DescriptionRow>
            <ModalRow />
            <ModalTools>
              <ModalButtonNormal
                type={'submit'}
                style={{ float: 'left', marginLeft: '0' }}
                disabled={!paramsOk}
                data-test-id="save-project-button"
                title={paramsOk ? 'Save changes' : 'Invalid or missing fields'}
              >
                SAVE
              </ModalButtonNormal>
              <ModalButtonPrimary
                type={'button'}
                style={{ float: 'right', marginRight: '0' }}
                onClick={closeDialog}
              >
                CANCEL
              </ModalButtonPrimary>
            </ModalTools>
          </form>
        </SpinnerDiv>
      </Modal>
    )
  }
}

const mapStateToProps = (state: Store.Complete) => ({
  userData: getUserInfo(state)
})

const mapDispatchToProps = (dispatch: Dispatch<Store.Complete>) => ({
  loadProject: (project: ProjectType) => dispatch(loadProject(project))
})

export default compose(
  withRouter,
  withCountries,
  connect(mapStateToProps, mapDispatchToProps),
  getProjectsQuery<GetProjectsProps>(),
  withProjectParameters
)(NewProjectDialog)



// WEBPACK FOOTER //
// ./src/Components/Projects/NewProjectDialog.tsx